import React from "react";
import { ArrowLeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../utils/translationUtils";
import { YieldAnalysisStatus } from "../../utils/yieldAnalysis";
import {
  EMPTY_VALUE,
  type PlotDesignEntry,
  type StripDesignRow,
} from "./trialPreviewMapper";

/** Labelled cell used by the header and Trial Status rows. */
export const InfoCell: React.FC<{
  label: string;
  children?: React.ReactNode;
}> = ({ label, children }) => (
  <div className="basic-info-content">
    <div className="info-label">{label}</div>
    <div className="info-value">{children ?? EMPTY_VALUE}</div>
  </div>
);

/** Section wrapper: 16px heading followed by its content. */
export const Section: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <div className="trial-preview-section">
    <h3>{title}</h3>
    {children}
  </div>
);

/** Shown in place of a section's content when there is nothing to list yet. */
const EmptyState: React.FC<{ testId: string }> = ({ testId }) => {
  const { t } = useTranslation();

  return (
    <p className="preview-empty" data-testid={testId}>
      {formatTranslation(t("trialPreview.noData"))}
    </p>
  );
};

/**
 * Drawer title for the protocol view: a back arrow immediately before the
 * protocol's name. The drawer's own close control stays to the right of it.
 */
export const ProtocolViewTitle: React.FC<{
  name?: string;
  onBack: () => void;
}> = ({ name, onBack }) => {
  const { t } = useTranslation();

  return (
    <span className="preview-title-with-back">
      <button
        type="button"
        className="preview-back-btn"
        data-testid="back-to-trial-btn"
        aria-label={formatTranslation(t("trialPreview.backToTrial"))}
        onClick={onBack}
      >
        <ArrowLeftOutlined />
      </button>
      <span className="preview-title-text">{name}</span>
    </span>
  );
};

/** The linked protocol, with the action that swaps it into this sidebar. */
export const LinkedProtocolSection: React.FC<{
  protocolName?: string;
  canView: boolean;
  onView: () => void;
}> = ({ protocolName, canView, onView }) => {
  const { t } = useTranslation();

  return (
    <Section title={formatTranslation(t("trialPreview.linkedProtocol"))}>
      <div className="linked-protocol-card">
        <span className="protocol-name">{protocolName ?? EMPTY_VALUE}</span>
        {canView && (
          <button
            type="button"
            className="preview-link"
            data-testid="view-protocol-btn"
            onClick={onView}
          >
            {formatTranslation(t("trialPreview.view"))}
            <RightOutlined />
          </button>
        )}
      </div>
    </Section>
  );
};

/** Plot Design: one cell per plot group, three to a row. */
export const PlotDesignSection: React.FC<{ entries: PlotDesignEntry[] }> = ({
  entries,
}) => {
  const { t } = useTranslation();

  return (
    <Section title={formatTranslation(t("trialPreview.plotDesign"))}>
      {entries.length === 0 ? (
        <EmptyState testId="plot-design-empty" />
      ) : (
        <div className="plot-design-grid">
          {entries.map((entry) => (
            <div className="info-content" key={entry.label}>
              <h4>{entry.label}</h4>
              <p>{entry.value}</p>
              {entry.replicants && (
                <span className="replicants-tag">{entry.replicants}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

/** Strip Design: every strip with its own length, width and area. */
export const StripDesignSection: React.FC<{ rows: StripDesignRow[] }> = ({
  rows,
}) => {
  const { t } = useTranslation();

  return (
    <Section title={formatTranslation(t("trialPreview.stripDesign"))}>
      {rows.length === 0 ? (
        <EmptyState testId="strip-design-empty" />
      ) : (
        <table className="strip-design-table" data-testid="strip-design-table">
          <thead>
            <tr>
              <th>{formatTranslation(t("trialPreview.stripTable.strip"))}</th>
              <th>{formatTranslation(t("trialPreview.stripTable.length"))}</th>
              <th>{formatTranslation(t("trialPreview.stripTable.width"))}</th>
              <th>{formatTranslation(t("trialPreview.stripTable.area"))}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((strip) => (
              <tr key={strip.label}>
                <td>{strip.label}</td>
                <td>{strip.length}</td>
                <td>{strip.width}</td>
                <td>{strip.area}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
};

/**
 * How each readiness status reads in the Data Readiness tag. PENDING means the
 * harvest file landed within the last 15 minutes and is still being processed;
 * FAILED and NOT FOUND are both "not ready", but only FAILED is worth reporting
 * to CW Explorer.
 */
const READINESS_TAGS: Record<
  YieldAnalysisStatus,
  { modifier: string; key: string }
> = {
  [YieldAnalysisStatus.Available]: {
    modifier: "ready",
    key: "trialPreview.yieldAnalysisReady",
  },
  [YieldAnalysisStatus.Pending]: {
    modifier: "pending",
    key: "trialPreview.yieldAnalysisProcessing",
  },
  [YieldAnalysisStatus.Failed]: {
    modifier: "not-ready",
    key: "trialPreview.yieldAnalysisNotReady",
  },
  [YieldAnalysisStatus.NotFound]: {
    modifier: "not-ready",
    key: "trialPreview.yieldAnalysisNotReady",
  },
};

/** Data Readiness tag, driven by yield-analysis readiness. */
export const ReadinessSection: React.FC<{ status: YieldAnalysisStatus }> = ({
  status,
}) => {
  const { t } = useTranslation();
  const tag =
    READINESS_TAGS[status] ?? READINESS_TAGS[YieldAnalysisStatus.NotFound];

  return (
    <Section title={formatTranslation(t("trialPreview.dataReadiness"))}>
      <span
        className={`readiness-tag ${tag.modifier}`}
        data-testid="yield-readiness-tag"
        data-status={status}
      >
        {formatTranslation(t(tag.key))}
      </span>
    </Section>
  );
};
